"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

import {
  getStatusText,
  type StatusIndicatorType,
} from "@/lib/betterstack-status-client";

interface StatusIndicatorProps {
  /**
   * Size variant of the indicator
   * - "sm": Small variant for inline use
   * - "default": Default size for footer
   */
  size?: "sm" | "default";
  /**
   * Whether to show a link to the status page
   */
  showLink?: boolean;
}

// Toast blue color (#006BFF)
const TOAST_BLUE = "#006BFF";

function getStatusColor(indicator: StatusIndicatorType): {
  bg: string;
  text: string;
  style?: React.CSSProperties;
} {
  switch (indicator) {
    case "operational":
      // Use same blue as toast (#006BFF)
      return {
        bg: "",
        text: "",
        style: { backgroundColor: TOAST_BLUE, color: TOAST_BLUE },
      };
    case "degraded":
      return { bg: "bg-amber-500", text: "text-amber-500" };
    case "downtime":
      return { bg: "bg-red-500", text: "text-red-500" };
    case "maintenance":
      return { bg: "bg-blue-500", text: "text-blue-500" };
    default:
      return { bg: "bg-muted-foreground", text: "text-muted-foreground" };
  }
}

const UNKNOWN_COLOR: ReturnType<typeof getStatusColor> = {
  bg: "bg-muted-foreground",
  text: "text-muted-foreground",
};

export function ClientStatusIndicator({
  size = "default",
  showLink = true,
}: StatusIndicatorProps) {
  const [status, setStatus] = useState<StatusIndicatorType | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchStatus() {
      try {
        const response = await fetch("/api/status");
        if (response.ok) {
          const data = await response.json();
          setStatus(data.indicator);
        }
      } catch {
        // Fail silently
      } finally {
        setIsLoading(false);
      }
    }

    void fetchStatus();
  }, []);

  const colors = status ? getStatusColor(status) : UNKNOWN_COLOR;
  const text = status
    ? getStatusText(status)
    : isLoading
      ? "Checking system status"
      : "Status unknown";

  const indicator = (
    <div className="flex items-center gap-2">
      {size === "sm" ? (
        <div
          className={`size-2 animate-pulse rounded-full ${colors.bg}`}
          style={
            colors.style
              ? { backgroundColor: colors.style.backgroundColor }
              : undefined
          }
          aria-hidden="true"
        />
      ) : (
        <div
          className={`rounded-full ${colors.bg}`}
          style={{
            width: 8,
            height: 8,
            ...(colors.style
              ? { backgroundColor: colors.style.backgroundColor }
              : {}),
          }}
          aria-hidden="true"
        />
      )}
      <span
        className={
          size === "sm"
            ? "text-muted-foreground text-xs"
            : `text-sm ${colors.text}`
        }
        style={
          colors.style && size !== "sm"
            ? { color: colors.style.color }
            : undefined
        }
      >
        {text}
      </span>
    </div>
  );

  if (showLink) {
    return (
      <Link
        href="https://status.terac.com"
        target="_blank"
        rel="noopener noreferrer"
        className="block w-fit transition-opacity hover:opacity-80"
        aria-label={`System status: ${text}. Click to view status page.`}
      >
        {indicator}
      </Link>
    );
  }

  return indicator;
}
